<template>
<div class="fabriki-products-grids">
  <template v-if="titleH1">
    <h1 id="fabriki-products-grids" class="fabriki-products-grids__title h2">{{ titleH1 }}</h1>
  </template>
  <template v-if="!titleH1">
    <div id="fabriki-products-grids" class="fabriki-products-grids__title h2">Предметы мебели</div>
  </template>

  <div class="fabriki-products-grids__controls">
    <div class="fabriki-products-grids__tags">
      <template
        v-for="(section, index) in filteringEmptyGroups"
        :key="`section-tag-${index}`"
      >
        <div
          :class="{'active': Boolean(activeSectionId === section.id)}"
          @click="() => changeActiveSectionIds(section.id)"
        >
          {{ section.name }}
        </div>
      </template>
    </div>
  </div>

  <template
    v-if="!loading"
    v-for="(section, index) in sections"
    :key="`section-products-${index}`"
  >
    <div
      :id="`products-section-${ section.id }`"
      :data-section-id="section.id"
      class="fabriki-products-section"
    >
      <div class="fabriki-products-section__title">{{ section.name }}</div>
      <div class="fabriki-products-section__items">
        <ProductCard
          v-for="(product) in section.products"
          :key="`product-${ product.slug }`"
          :product="product"

          :is-collection="true"
        />
      </div>
    </div>
  </template>

  <template v-if="loading">
    <div class="fabriki-products-grids__items">
      <div class="--loading-card loading-skeleton"/>
      <div class="--loading-card loading-skeleton"/>
      <div class="--loading-card loading-skeleton"/>
      <div class="--loading-card loading-skeleton"/>
    </div>
  </template>
</div>
</template>

<script setup lang="ts">
import type { IProduct } from '#sitis/internal/controllers/products/models/Product';
import type { ICharacteristicGroup, IProductsSection } from '~/pages/fabriki/[...slug].vue';
import { scrollToElement } from '~/utils/scrollToElement';
import ProductCard from "~/components/catalog/ProductCard.vue";
import type { Ref } from 'vue';

interface IProps {
  titleH1: string;
  loading: boolean;
  sections: IProductsSection[];
  groups: ICharacteristicGroup[];
}

const props = withDefaults(defineProps<IProps>(), {
  titleH1: '',
  loading: false,
  groups: () => [],
  sections: () => [],
});
const emits = defineEmits(['changeFilter']);

const activeSectionId: Ref<number> = ref(props.groups[0]?.id);
const changeActiveSectionIds = (id: number | undefined) => {
  scrollToElement(`#products-section-${ id }`);
};

const watchEventScroll = () => {
  const sections = document.querySelectorAll('.fabriki-products-section');
  let visibleSection: any = null;
  sections.forEach((section, idx) => {
    const position = section.getBoundingClientRect();
    const top = position.top - document.querySelector('.header')!.getBoundingClientRect().height - 60;
    const isVisible = Boolean(top <= 0 && position.bottom >= 0);
    if (isVisible) {
      visibleSection = section;
    }
  });
  if (!visibleSection) {
    return;
  }

  const sectionId = visibleSection.getAttribute('data-section-id');
  activeSectionId.value = Number.parseFloat(sectionId || '1');
};

// фильтрация на пустые группы
const filteringEmptyGroups = computed(() => {
  if (!props.groups || !props.sections) {
    return []
  }
  let _groups: ICharacteristicGroup[] = props.groups
  const sectionsIds = props.sections.map((section) => section.id)

  _groups = _groups.filter((group) => sectionsIds.includes(group.id))

  return _groups;
});

onMounted(() => {
  window.addEventListener('scroll', watchEventScroll);
});
onBeforeUnmount(() => {
  if (window && window?.removeEventListener) {
    window.removeEventListener('scroll', watchEventScroll);
  }
});
</script>

<style scoped lang="scss">
.fabriki-products-grids .fabriki-products-section {
    margin-top: 80px;
  }
.fabriki-products-grids__title {
  margin-bottom: 32px;
}
.fabriki-products-grids__controls {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 32px;
  position: sticky;
  top: 127px;
  z-index: 10;
  padding: 10px 0;
  background: white;
}
.fabriki-products-grids__tags {
  display: flex;
  flex-wrap: wrap;
  margin-top: -16px;
  margin-left: -16px;

  & > * {
    margin-top: 16px;
    margin-left: 16px;
    border-radius: 8px;
    border: 1px solid #EBEBEB;
    padding: 10px 24px;
    box-sizing: border-box;
    cursor: pointer;

    color: #000;
    font-size: 16px;
    font-weight: 500;
    line-height: 100%;

    &:hover {
      background-color: #F4F3F1;
      color: #000000;
    }
    &.active {
      background-color: #404040;
      color: white;
    }
  }
}
.fabriki-products-grids__items {
  display: flex;
  flex-wrap: wrap;
  margin-top: -64px;
  margin-left: -32px;
  & > * {
    width: calc(100% / 4 - 32px);
    margin-top: 64px;
    margin-left: 32px;
  }
  .--loading-card {
    height: 413px;
    border-radius: 20px;
    overflow: hidden;
  }
}


.fabriki-products-section {}
.fabriki-products-section__title {
  margin-bottom: 40px;

  font-weight: 600;
  font-size: 24px;
  line-height: 29px;
  color: #333333;
}
.fabriki-products-section__items {
  display: flex;
  flex-wrap: wrap;
  margin-top: -64px;
  margin-left: -32px;
  & > * {
    width: calc(100% / 4 - 32px);
    margin-top: 64px;
    margin-left: 32px;
  }
}

@media (max-width: 1699px) {
  .fabriki-products-grids__items > *,
  .fabriki-products-section__items > * {
    width: calc(100% / 3 - 32px);
  }
}
@media (max-width: 1499px) {
  .fabriki-products-grids__items, .fabriki-products-section__items {
    margin-left: -20px;
  }
  .fabriki-products-grids__items > *,
  .fabriki-products-section__items > * {
    width: calc(100% / 3 - 20px);
    margin-left: 20px;
  }
}
@media (max-width: 1199px) {
  .fabriki-products-grids__tags {
    flex-wrap: initial;
    overflow: auto;
    & > * {
      white-space: nowrap;
    }
  }
  .fabriki-products-grids__items, .fabriki-products-section__items {
    margin-top: -16px;
    margin-left: -16px;
  }
  .fabriki-products-grids__items > *,
  .fabriki-products-section__items > * {
    width: calc(50% - 16px);
    margin-top: 16px;
    margin-left: 16px;
  }
  .fabriki-products-grids__controls {
    top: 117px;
  }
}
@media (max-width: 1023px) {
  .fabriki-products-grids__controls {
    top: 80px;
  }
  .fabriki-products-grids .fabriki-products-section {
    margin-top: 32px;
  }
  .fabriki-products-grids__items, .fabriki-products-section__items {
    margin-left: 0;
    & > * {
      width: 100%;
      margin-left: 0;
    }
  }
}
</style>
